import React from 'react';
import { AssetUrls } from 'config/assets';
import { SetInfo } from 'store/services/sets/setsApi';
import SetMultiSelect from 'components/AddSetModal/SetMultiSelect';

export interface MatchSummary {
  matchingCards: number;
  totalCards: number;
  matchingPackages: number;
  totalPackages: number;
}

export type DisplayMode = 'comfortable' | 'compact' | 'list';

export const SORT_OPTIONS = [
  { value: 'default', label: 'Default' },
  { value: 'value-desc', label: 'Value: High to Low' },
  { value: 'value-asc', label: 'Value: Low to High' },
  { value: 'cards-desc', label: 'Most Cards' },
  { value: 'newest-desc', label: 'Newest Members' },
];

export const DEFAULT_DISPLAY_MODE: DisplayMode = 'comfortable';
export const DEFAULT_SORT_VALUE = 'default';

export interface SendToolbarProps {
  onSearch?: (query: string) => void;
  displayMode?: DisplayMode;
  onDisplayModeChange?: (mode: DisplayMode) => void;
  initialSearchValue?: string;
  searchType?: 'card' | 'user';
  onSearchTypeChange?: (type: 'card' | 'user') => void;
  globalAutoMatch?: boolean;
  onGlobalAutoMatchToggle?: (enabled: boolean) => void;
  matchSummary?: MatchSummary;
  isMatchIndexLoading?: boolean;
  hasMatchIndexError?: boolean;
  onRetryMatchIndex?: () => void;
  priceMin?: number;
  priceMax?: number;
  onPriceMinChange?: (value: number | undefined) => void;
  onPriceMaxChange?: (value: number | undefined) => void;
  sortValue?: string;
  onSortChange?: (value: string) => void;
  isSortLoading?: boolean;
  onExportCsv?: () => void;
  isExporting?: boolean;
  hasResults?: boolean;
  selectedSets?: SetInfo[];
  onSelectedSetsChange?: (sets: SetInfo[]) => void;
  /** Collapse all / Expand all for trader want lists; the button renders only with a handler. */
  collapseAll?: boolean;
  onCollapseAllToggle?: () => void;
}

export const ChevronSvg: React.FC<{ open: boolean }> = ({ open }) => (
  <svg
    className={`send-sort-chevron ${open ? 'open' : ''}`}
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    aria-hidden="true"
  >
    <path d="M3 4.5L6 7.5L9 4.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// -- Control renderers shared between mobile and desktop layouts --
// State lives in the parent (SendPage2), so each control behaves identically
// regardless of where it's placed.

export function SearchTypeToggle({
  searchType,
  onSearchTypeChange,
  attached,
}: {
  searchType: 'card' | 'user';
  onSearchTypeChange?: (type: 'card' | 'user') => void;
  // attached: visually attached to the search input on its right (shared border-radius)
  attached: boolean;
}) {
  return (
    <div className={`send-search-type-toggle ${attached ? 'attached' : 'detached'}`}>
      <button
        type="button"
        className={`send-search-type-btn ${searchType === 'card' ? 'active' : ''}`}
        onClick={() => onSearchTypeChange?.('card')}
      >
        <img src="https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/icons/card.svg" alt="" className="send-search-type-icon" />
        Card
      </button>
      <button
        type="button"
        className={`send-search-type-btn ${searchType === 'user' ? 'active' : ''}`}
        onClick={() => onSearchTypeChange?.('user')}
      >
        <img src="https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/icons/person.svg" alt="" className="send-search-type-icon" />
        Trader
      </button>
    </div>
  );
}

export function SearchInput({
  searchType,
  initialSearchValue,
  onSearch,
  attached,
}: {
  searchType: 'card' | 'user';
  initialSearchValue: string;
  onSearch?: (query: string) => void;
  // attached: visually attached to the toggle on its left
  attached: boolean;
}) {
  return (
    <div className={`send-search-input-wrapper ${attached ? 'attached' : 'detached'}`}>
      <svg className="send-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="11" cy="11" r="8" />
        <path d="m21 21-4.35-4.35" />
      </svg>
      <input
        type="text"
        className="send-search-input"
        placeholder={searchType === 'card' ? 'Search card name' : 'Search username'}
        value={initialSearchValue}
        onChange={(e) => onSearch?.(e.target.value)}
      />
      {initialSearchValue && (
        <button
          className="send-search-clear"
          onClick={() => onSearch?.('')}
          aria-label="Clear search"
          type="button"
        >
          &#x2715;
        </button>
      )}
    </div>
  );
}

export function SetFilter({
  selectedSets,
  onSelectedSetsChange,
}: {
  selectedSets: SetInfo[];
  onSelectedSetsChange?: (sets: SetInfo[]) => void;
}) {
  if (!onSelectedSetsChange) return null;
  return (
    <div className="send-set-filter">
      <SetMultiSelect
        selectedSets={selectedSets}
        onChange={onSelectedSetsChange}
        maxSelections={5}
        label="Set"
        hideCounter
        placeholder="Filter sets"
      />
    </div>
  );
}

// PriceFilter was extracted into a shared component so the Trade Alerts filter
// row can reuse the exact same Min/Max convention. Re-exported here under the
// original name so existing Send-toolbar importers keep working unchanged.
export { PriceRangeFilter as PriceFilter } from 'components/PriceRangeFilter/PriceRangeFilter';

// Auto-Match supports two presentations:
//  - full: label + toggle + summary text
//  - compact: label + toggle only (summary suppressed for narrower toolbars)
// Per the spec, the LABEL is never dropped on desktop — only the summary may hide.
export function AutoMatchControl({
  globalAutoMatch,
  onGlobalAutoMatchToggle,
  matchSummary,
  isMatchIndexLoading,
  hasMatchIndexError,
  onRetryMatchIndex,
  showSummary = true,
}: {
  globalAutoMatch: boolean;
  onGlobalAutoMatchToggle?: (enabled: boolean) => void;
  matchSummary?: MatchSummary;
  isMatchIndexLoading?: boolean;
  hasMatchIndexError?: boolean;
  onRetryMatchIndex?: () => void;
  showSummary?: boolean;
}) {
  return (
    <div className={`send-auto-match-global ${hasMatchIndexError ? 'has-error' : ''}`}>
      <label className="send-auto-match-label">
        <span className="send-auto-match-icon" aria-hidden="true">&#9889;</span>
        <span className="send-auto-match-text">Auto-Match</span>
        <div className="send-auto-match-toggle">
          <input
            type="checkbox"
            checked={globalAutoMatch}
            onChange={(e) => onGlobalAutoMatchToggle?.(e.target.checked)}
            disabled={isMatchIndexLoading}
          />
          <span className={`send-toggle-slider ${isMatchIndexLoading ? 'loading' : ''}`} />
        </div>
      </label>

      {isMatchIndexLoading && (
        <span className="send-auto-match-loading">
          <span className="loading-spinner-small" />
          Loading...
        </span>
      )}

      {hasMatchIndexError && !isMatchIndexLoading && (
        <span className="send-auto-match-error">
          <span className="error-icon">!</span>
          <button
            className="send-auto-match-retry"
            onClick={onRetryMatchIndex}
          >
            Retry
          </button>
        </span>
      )}

      {showSummary && globalAutoMatch && matchSummary && !isMatchIndexLoading && !hasMatchIndexError && (
        <span className="send-auto-match-summary">
          {matchSummary.matchingCards} cards in {matchSummary.matchingPackages} packages
        </span>
      )}
    </div>
  );
}

export function DensityDropdown({
  displayMode,
  onDisplayModeChange,
}: {
  displayMode: DisplayMode;
  onDisplayModeChange?: (mode: DisplayMode) => void;
}) {
  return (
    <select
      className="send-dropdown"
      value={displayMode}
      onChange={(e) => onDisplayModeChange?.(e.target.value as DisplayMode)}
      aria-label="Card density"
    >
      <option value="comfortable">Comfortable</option>
      <option value="compact">Compact</option>
      <option value="list">List</option>
    </select>
  );
}

export function DownloadButton({
  onExportCsv,
  isExporting,
  hasResults,
}: {
  onExportCsv?: () => void;
  isExporting?: boolean;
  hasResults?: boolean;
}) {
  if (!hasResults || !onExportCsv) return null;
  return (
    <button
      className="send-export-button"
      onClick={onExportCsv}
      disabled={isExporting}
      title="Export filtered results as CSV"
      aria-label="Export to CSV"
    >
      {isExporting ? (
        <span className="loading-spinner-small" />
      ) : (
        <img
          src={`${AssetUrls.base}/assets/icons/download.svg`}
          className="send-export-icon"
          alt=""
        />
      )}
    </button>
  );
}
